import { type JSX, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AgentsCard } from '@/components/admin/agents-card';
import { Icon } from '@/components/icons';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  Progress,
  Skeleton,
  StatusPill,
  type CampaignStatus,
} from '@/components/ui';
import {
  openImpersonatedTab,
  useActivateCustomer,
  useAssignCustomerPlan,
  useChangeTenantType,
  useCustomerDetail,
  useImpersonate,
  useSetComplianceTemplates,
  useSetCreditLimit,
  useSetFeatureFollowupSMS,
  useSetFeatureSMS,
  useSetFeatureVoice,
  useSetLowBalanceThreshold,
  useSetSpamguardLevel,
  useSetTTSCap,
  useSuspendCustomer,
  useTopUpCustomer,
  type CustomerCampaignSummary,
  type CustomerPlanRef,
  type CustomerSubUser,
  type CustomerTTSBudget,
  type SpamguardLevel,
  type TenantTypeChangeable,
} from '@/hooks/useAdminCustomers';
import { useAuth } from '@/hooks/useAuth';
import {
  useAddMembership,
  useAssignableTenants,
  useRemoveMembership,
  useUserMemberships,
  type MembershipRole,
} from '@/hooks/useAdminMemberships';
import { usePlans } from '@/hooks/useAdminPlans';
import { useRateCards } from '@/hooks/useAdminRateCards';
import {
  useAssignTenantRateCard,
  useReorderTenantRateCard,
  useTenantRateCards,
  useTestPrefix,
  useUnassignTenantRateCard,
  type TenantRateCardAssignment,
} from '@/hooks/useAdminTenantRateCards';
import { StrategyBadge } from '@/components/routing-strategy-help';
import {
  useAssignVoiceRateCard,
  useTenantVoiceRateCards,
  useUnassignVoiceRateCard,
  useVoiceRateCards,
} from '@/hooks/useVoiceRateCards';
import { ApiError } from '@/lib/api';
import { formatCurrency, formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Reseller admin → customer drilldown. Lives at /admin/customers/:id and is
// gated by the same RequireReseller route guard as the list page. The API
// returns header, 30-day stats, sub-users, and recent campaigns in a single
// payload (see useCustomerDetail) so the page paints without waterfall.
// ---------------------------------------------------------------------------
export default function AdminCustomerDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const navigate = useNavigate();

  const detail = useCustomerDetail(id);
  const suspend = useSuspendCustomer();
  const activate = useActivateCustomer();
  const impersonate = useImpersonate();
  const changeType = useChangeTenantType();
  const setCreditLimit = useSetCreditLimit();
  const setLowBalanceThreshold = useSetLowBalanceThreshold();
  const topUp = useTopUpCustomer();
  const [topUpOpen, setTopUpOpen] = useState(false);
  const { user } = useAuth();
  const isPlatformAdmin = user?.tenantType === 'platform';
  const [confirm, setConfirm] = useState<'suspend' | 'activate' | null>(null);
  const [typeConfirm, setTypeConfirm] = useState<TenantTypeChangeable | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);
  // Credit-limit editor: null = closed, number = the draft value the
  // operator is typing (init from current credit_limit on open).
  const [creditEdit, setCreditEdit] = useState<number | null>(null);
  const [creditError, setCreditError] = useState<string | null>(null);
  // Low-balance-threshold editor follows the same null = closed convention.
  // 0 disables the in-app banner + email alerts (PR #122).
  const [thresholdEdit, setThresholdEdit] = useState<number | null>(null);
  const [thresholdError, setThresholdError] = useState<string | null>(null);

  if (detail.isLoading) {
    return <DetailSkeleton />;
  }

  if (detail.error || !detail.data) {
    const isMissing =
      detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div className="px-8 py-8 max-w-[1200px] mx-auto">
        <Card padding="lg">
          <div className="text-center py-10">
            <p className="text-[14px] text-text-2 font-medium">
              {isMissing ? t('acd_not_found_title') : t('acd_load_error_title')}
            </p>
            <p className="text-[13px] text-text-3 mt-1">
              {isMissing ? t('acd_not_found_sub') : t('acd_load_error_sub')}
            </p>
            <Button className="mt-5" variant="secondary" onClick={() => navigate('/admin/customers')}>
              <Icon.chevronLeft width="14" height="14" />
              {t('acd_back')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const { customer, stats_30d, users, recent_campaigns } = detail.data;
  const isSuspended = customer.status === 'suspended';

  const onConfirm = async () => {
    try {
      if (confirm === 'suspend') await suspend.mutateAsync(id);
      else if (confirm === 'activate') await activate.mutateAsync(id);
    } catch {
      // The list+detail queries refetch automatically; surface failures
      // through the next paint instead of trapping the operator in a modal.
    } finally {
      setConfirm(null);
    }
  };

  const onConfirmCreditLimit = async () => {
    if (creditEdit === null) return;
    setCreditError(null);
    if (!Number.isFinite(creditEdit) || creditEdit < 0) {
      setCreditError(t('acd_credit_err_negative'));
      return;
    }
    try {
      await setCreditLimit.mutateAsync({ customerId: id, creditLimit: creditEdit });
      setCreditEdit(null);
    } catch (err) {
      if (err instanceof ApiError) {
        setCreditError(err.message ?? t('acd_credit_err_generic'));
      } else {
        setCreditError(t('acd_credit_err_generic'));
      }
    }
  };

  const onConfirmThreshold = async () => {
    if (thresholdEdit === null) return;
    setThresholdError(null);
    if (!Number.isFinite(thresholdEdit) || thresholdEdit < 0) {
      setThresholdError(t('acd_threshold_err_negative'));
      return;
    }
    try {
      await setLowBalanceThreshold.mutateAsync({ customerId: id, threshold: thresholdEdit });
      setThresholdEdit(null);
    } catch (err) {
      if (err instanceof ApiError) {
        setThresholdError(err.message ?? t('acd_threshold_err_generic'));
      } else {
        setThresholdError(t('acd_threshold_err_generic'));
      }
    }
  };

  const onConfirmTypeChange = async () => {
    if (typeConfirm === null) return;
    setTypeError(null);
    try {
      await changeType.mutateAsync({ tenantId: id, newType: typeConfirm });
      setTypeConfirm(null);
    } catch (err) {
      // Validation errors carry the concrete reason in `message`
      // (e.g. "cannot demote tenant with N sub-tenant(s)") — surface
      // it inline in the modal instead of dismissing.
      if (err instanceof ApiError) {
        setTypeError(err.message ?? t('acd_type_change_error'));
      } else {
        setTypeError(t('acd_type_change_error'));
      }
    }
  };

  const onImpersonate = async () => {
    try {
      const res = await impersonate.mutateAsync(id);
      openImpersonatedTab(res.access_token, res.customer_id, res.customer_name);
    } catch {
      // Same rationale as above; a toast layer is the right home for this
      // when we add one.
    }
  };

  const busy = suspend.isPending || activate.isPending;

  return (
    <div className="flex flex-col h-full">
      {isSuspended && (
        <Banner
          tone="warn"
          title={t('acd_suspended_banner_title')}
          body={t('acd_suspended_banner_body', { name: customer.name })}
          action={
            <Button size="sm" disabled={busy} onClick={() => setConfirm('activate')}>
              {t('ac_action_activate')}
            </Button>
          }
        />
      )}

      <div className="px-8 py-8 max-w-[1200px] mx-auto w-full flex-1 overflow-y-auto">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-text-3 mb-4">
          <Icon.chevronLeft width="14" height="14" />
          <Link to="/admin/customers" className="text-text-3 hover:text-text">
            {t('acd_breadcrumb_root')}
          </Link>
          <span className="text-text-4">/</span>
          <span className="text-text-2">{customer.name}</span>
        </nav>

        {/* Header */}
        <header className="flex items-start gap-4 mb-6 flex-wrap">
          <CustomerAvatar name={customer.name} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="display text-[26px] font-semibold tracking-tight text-text leading-none m-0">
                {customer.name}
              </h1>
              <Badge
                variant={
                  customer.status === 'active'
                    ? 'success'
                    : customer.status === 'suspended'
                      ? 'warn'
                      : 'neutral'
                }
              >
                {t(`ac_status_${customer.status}`)}
              </Badge>
              {/* Tenant type — a badge for everyone, click-to-change for
                  platform admins only. Lets the operator promote a
                  customer to reseller (or demote) without dropping to
                  SQL. The backend gate (RequirePlatform) makes the
                  visibility check defense-in-depth, not security. */}
              {isPlatformAdmin && customer.type !== 'platform' ? (
                <button
                  type="button"
                  onClick={() =>
                    setTypeConfirm(customer.type === 'reseller' ? 'customer' : 'reseller')
                  }
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-surface-2 border border-line hover:border-volt-500 hover:bg-surface-3 text-text-2 transition-colors"
                  title={t('acd_type_change_title')}
                >
                  <span className="capitalize">{t(`tenant_type_${customer.type}`)}</span>
                  <Icon.chevronDown width="11" height="11" />
                </button>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium bg-surface-2 border border-line text-text-3 capitalize">
                  {t(`tenant_type_${customer.type}`)}
                </span>
              )}
              <span className="mono text-[11.5px] text-text-4 px-2 py-0.5 bg-surface-2 border border-line rounded-sm">
                {customer.uuid}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-2 text-[13px] text-text-3 flex-wrap">
              {customer.owner_email && (
                <span className="mono">{customer.owner_email}</span>
              )}
              {customer.country && (
                <>
                  <span>·</span>
                  <span>{customer.country}</span>
                </>
              )}
              <span>·</span>
              <span>
                {t('acd_joined', {
                  when: formatRelativeTime(customer.created_at, lang),
                })}
              </span>
              <span>·</span>
              <span className="num text-text-2 font-medium">
                {formatCurrency(customer.balance, lang)}
              </span>
              {customer.credit_limit > 0 && (
                <>
                  <span>·</span>
                  <span className="text-text-3 text-[12px]">
                    {t('acd_credit_post_paid', {
                      limit: formatCurrency(customer.credit_limit, lang),
                    })}
                  </span>
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  setCreditEdit(customer.credit_limit);
                  setCreditError(null);
                }}
                className="text-[12px] text-volt-500 font-medium hover:underline"
              >
                {t('acd_credit_edit')}
              </button>
              <span>·</span>
              <span className="text-text-3 text-[12px]">
                {t('acd_threshold_summary', {
                  threshold: formatCurrency(customer.low_balance_threshold, lang),
                })}
              </span>
              <button
                type="button"
                onClick={() => {
                  setThresholdEdit(customer.low_balance_threshold);
                  setThresholdError(null);
                }}
                className="text-[12px] text-volt-500 font-medium hover:underline"
              >
                {t('acd_threshold_edit')}
              </button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="secondary" onClick={() => setTopUpOpen(true)}>
              <Icon.plus width="14" height="14" />
              {t('ac_action_top_up')}
            </Button>
            {customer.status === 'active' && (
              <Button
                variant="secondary"
                disabled={impersonate.isPending || busy}
                onClick={() => void onImpersonate()}
              >
                <Icon.eye width="14" height="14" />
                {t('ac_action_view_as')}
              </Button>
            )}
            {customer.status === 'active' ? (
              <Button
                variant="secondary"
                className="text-warn hover:bg-warn/10"
                disabled={busy}
                onClick={() => setConfirm('suspend')}
              >
                {t('ac_action_suspend')}
              </Button>
            ) : customer.status === 'suspended' ? (
              <Button disabled={busy} onClick={() => setConfirm('activate')}>
                {t('ac_action_activate')}
              </Button>
            ) : null}
          </div>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi
            label={t('acd_kpi_sent')}
            value={stats_30d.sent.toLocaleString(lang)}
          />
          <Kpi
            label={t('acd_kpi_delivery_rate')}
            value={
              stats_30d.sent > 0
                ? `${(stats_30d.delivery_rate * 100).toFixed(1)}%`
                : '—'
            }
          />
          <Kpi
            label={t('acd_kpi_spend')}
            value={formatCurrency(stats_30d.spend, lang)}
          />
          <Kpi label={t('acd_kpi_margin')} value="—" mutedHint={t('acd_kpi_margin_hint')} />
        </div>

        {/* Body — 2 columns. The single-Overview layout already shows
            Activity (Recent campaigns), Plan & rates, and Sub-users
            in their own cards, so the placeholder tab strip we used
            to render here was just visual debt. If a real "split into
            tabs" need surfaces later (per-customer audit log filter
            is the only one with new data), reintroduce it then. */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          <div className="flex flex-col gap-4 min-w-0">
            <RecentCampaignsCard campaigns={recent_campaigns} lang={lang} />
            <SubUsersCard users={users} />
            {/* Agent commissions: platform-only surface, only meaningful
                when the tenant is a reseller (agents commission against
                a reseller's customers, not against another customer). */}
            {isPlatformAdmin && customer.type === 'reseller' && (
              <AgentsCard resellerID={id} lang={lang} />
            )}
          </div>
          <div className="flex flex-col gap-4">
            <PlanCard customerId={id} currentPlan={detail.data.plan} lang={lang} />
            <RateCardsCard tenantId={id} />
            <VoiceRateCardCard tenantId={id} />
            <TTSBudgetCard customerId={id} budget={detail.data.tts_budget} lang={lang} />
            {/* SpamGuard posture: platform-only. A reseller marking its own
                tenant 'trusted' would bypass spam screening at launch, so
                the control never renders for non-platform admins. */}
            {isPlatformAdmin && customer.type !== 'platform' && (
              <SpamguardLevelCard customerId={id} level={customer.spamguard_level ?? 'normal'} />
            )}
            <FeatureVoiceToggleCard customerId={id} enabled={customer.feature_voice} />
            <FeatureSMSToggleCard customerId={id} enabled={customer.feature_sms} />
            <FollowupSMSToggleCard customerId={id} enabled={customer.feature_followup_sms} />
            <ComplianceTemplatesCard
              customerId={id}
              help={customer.help_template ?? ''}
              stop={customer.stop_template ?? ''}
              start={customer.start_template ?? ''}
            />
            <AccountCard customer={customer} lang={lang} />
            <ActivityPlaceholderCard />
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirm !== null}
        title={
          confirm === 'suspend'
            ? t('ac_suspend_title')
            : t('ac_activate_title')
        }
        description={
          confirm === 'suspend'
            ? t('ac_suspend_desc', { name: customer.name })
            : t('ac_activate_desc', { name: customer.name })
        }
        confirmLabel={
          confirm === 'suspend' ? t('ac_action_suspend') : t('ac_action_activate')
        }
        cancelLabel={t('ac_cancel')}
        tone={confirm === 'suspend' ? 'danger' : 'primary'}
        busy={busy}
        onConfirm={() => void onConfirm()}
        onClose={() => setConfirm(null)}
      />

      {/* Credit-limit editor — single numeric input wrapped in a confirm
          modal shape. Inline rather than its own component because it's
          the only place in the app that needs this exact pattern; if
          another field-edit modal lands later, abstract then. */}
      {creditEdit !== null && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="credit-modal-title"
          onClick={() => {
            if (!setCreditLimit.isPending) {
              setCreditEdit(null);
              setCreditError(null);
            }
          }}
        >
          <div
            className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[440px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-line">
              <h2 id="credit-modal-title" className="display text-[16px] font-semibold text-text">
                {t('acd_credit_modal_title')}
              </h2>
            </div>
            <div className="px-5 py-4 text-[13px] text-text-2 leading-relaxed">
              <p className="mb-3">{t('acd_credit_modal_desc', { name: customer.name })}</p>
              <label className="block">
                <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
                  {t('acd_credit_modal_label')}
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  autoFocus
                  value={creditEdit}
                  onChange={(e) => setCreditEdit(Number(e.target.value))}
                  disabled={setCreditLimit.isPending}
                  className={cn(
                    'h-10 w-full px-3 text-sm bg-surface text-text border rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50',
                    creditError ? 'border-danger' : 'border-line-strong',
                  )}
                />
                <p className="text-[11.5px] text-text-3 mt-1.5">
                  {t('acd_credit_modal_hint')}
                </p>
              </label>
              {creditError && (
                <p className="text-[12px] text-danger mt-2">{creditError}</p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setCreditEdit(null);
                  setCreditError(null);
                }}
                disabled={setCreditLimit.isPending}
              >
                {t('ac_cancel')}
              </Button>
              <Button
                onClick={() => void onConfirmCreditLimit()}
                disabled={setCreditLimit.isPending}
              >
                {setCreditLimit.isPending ? t('acd_credit_modal_saving') : t('acd_credit_modal_save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Low-balance-threshold editor — same modal shape as the credit-limit
          editor. Setting to 0 disables the in-app banner + email alerts
          (the cron filters out tenants whose balance is not below their
          threshold; a zero threshold is never "below" since balance is
          gated to >= 0 by the dispatcher). */}
      {thresholdEdit !== null && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="threshold-modal-title"
          onClick={() => {
            if (!setLowBalanceThreshold.isPending) {
              setThresholdEdit(null);
              setThresholdError(null);
            }
          }}
        >
          <div
            className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[440px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-line">
              <h2 id="threshold-modal-title" className="display text-[16px] font-semibold text-text">
                {t('acd_threshold_modal_title')}
              </h2>
            </div>
            <div className="px-5 py-4 text-[13px] text-text-2 leading-relaxed">
              <p className="mb-3">{t('acd_threshold_modal_desc', { name: customer.name })}</p>
              <label className="block">
                <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
                  {t('acd_threshold_modal_label')}
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  autoFocus
                  value={thresholdEdit}
                  onChange={(e) => setThresholdEdit(Number(e.target.value))}
                  disabled={setLowBalanceThreshold.isPending}
                  className={cn(
                    'h-10 w-full px-3 text-sm bg-surface text-text border rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50',
                    thresholdError ? 'border-danger' : 'border-line-strong',
                  )}
                />
                <p className="text-[11.5px] text-text-3 mt-1.5">
                  {t('acd_threshold_modal_hint')}
                </p>
              </label>
              {thresholdError && (
                <p className="text-[12px] text-danger mt-2">{thresholdError}</p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setThresholdEdit(null);
                  setThresholdError(null);
                }}
                disabled={setLowBalanceThreshold.isPending}
              >
                {t('ac_cancel')}
              </Button>
              <Button
                onClick={() => void onConfirmThreshold()}
                disabled={setLowBalanceThreshold.isPending}
              >
                {setLowBalanceThreshold.isPending ? t('acd_threshold_modal_saving') : t('acd_threshold_modal_save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={typeConfirm !== null}
        title={
          typeConfirm === 'reseller'
            ? t('acd_promote_title')
            : t('acd_demote_title')
        }
        description={
          (typeConfirm === 'reseller'
            ? t('acd_promote_desc', { name: customer.name })
            : t('acd_demote_desc', { name: customer.name })) +
          (typeError ? `\n\n${typeError}` : '')
        }
        confirmLabel={
          typeConfirm === 'reseller'
            ? t('acd_promote_action')
            : t('acd_demote_action')
        }
        cancelLabel={t('ac_cancel')}
        tone={typeConfirm === 'reseller' ? 'primary' : 'danger'}
        busy={changeType.isPending}
        onConfirm={() => void onConfirmTypeChange()}
        onClose={() => {
          setTypeConfirm(null);
          setTypeError(null);
        }}
      />

      {topUpOpen && (
        <TopUpModal
          customerName={customer.name}
          busy={topUp.isPending}
          onClose={() => setTopUpOpen(false)}
          onSubmit={async (amount, description) => {
            await topUp.mutateAsync({ customerId: id, amount, description });
            setTopUpOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual top-up modal. Stripe is offline; meanwhile the operator records
// off-platform payments here. Numeric input + free-text description (e.g.
// "Bank transfer ref #ABC123" or "Zelle 2026-05-06"). Audit log captures
// who/when/how-much automatically via the AuditAdmin middleware on the
// route, so this UI doesn't need an extra log call.
// ---------------------------------------------------------------------------
interface TopUpModalProps {
  customerName: string;
  busy: boolean;
  onSubmit: (amount: number, description: string) => Promise<void>;
  onClose: () => void;
}

function TopUpModal({ customerName, busy, onSubmit, onClose }: TopUpModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    setError(null);
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError(t('ac_topup_err_amount'));
      return;
    }
    if (parsed > 100_000) {
      setError(t('ac_topup_err_max'));
      return;
    }
    try {
      await onSubmit(parsed, description.trim());
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message ?? t('ac_topup_err_generic'));
      } else {
        setError(t('ac_topup_err_generic'));
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[440px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('ac_topup_title', { name: customerName })}
          </h2>
        </div>
        <div className="px-5 py-4 space-y-3 text-[13px] text-text-2">
          <p>{t('ac_topup_desc')}</p>
          <label className="block">
            <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
              {t('ac_topup_amount_label')}
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              autoFocus
              placeholder="100.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={busy}
              className={cn(
                'h-10 w-full px-3 text-sm bg-surface text-text border rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50',
                error ? 'border-danger' : 'border-line-strong',
              )}
            />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
              {t('ac_topup_description_label')}
            </span>
            <input
              type="text"
              maxLength={200}
              placeholder={t('ac_topup_description_placeholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
              className="h-10 w-full px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50"
            />
            <span className="text-[11.5px] text-text-3 mt-1.5 block">
              {t('ac_topup_description_hint')}
            </span>
          </label>
          {error && <p className="text-[12px] text-danger">{error}</p>}
        </div>
        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('ac_cancel')}
          </Button>
          <Button onClick={() => void handle()} disabled={busy}>
            {busy ? t('ac_topup_saving') : t('ac_topup_submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header avatar — same volt gradient as the list page so the operator sees
// the same identity at a glance.
// ---------------------------------------------------------------------------
function CustomerAvatar({ name }: { name: string }): JSX.Element {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      aria-hidden="true"
      className="w-14 h-14 rounded-xl grid place-items-center text-white text-[20px] font-bold flex-shrink-0"
      style={{ background: 'linear-gradient(135deg, var(--volt-500), var(--volt-700))' }}
    >
      {initial}
    </span>
  );
}

// ---------------------------------------------------------------------------
// KPI card. Sparklines are intentionally off in this iteration — the API
// doesn't return time-series yet (would need a daily rollup query); adding
// fake-shaped sparklines now would mislead the operator.
// ---------------------------------------------------------------------------
interface KpiProps {
  label: string;
  value: string;
  /** Dimmed value style + tooltip — used for metrics not yet computed
   *  server-side so the operator knows why they see "—". */
  mutedHint?: string;
}
function Kpi({ label, value, mutedHint }: KpiProps): JSX.Element {
  return (
    <Card padding="md">
      <div className="text-[12px] text-text-3 uppercase tracking-[0.04em] font-medium">
        {label}
      </div>
      <div
        className={cn(
          'num display text-[24px] font-semibold tracking-tight mt-2',
          mutedHint ? 'text-text-4' : 'text-text',
        )}
        title={mutedHint}
      >
        {value}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent campaigns card
// ---------------------------------------------------------------------------
interface RecentCampaignsCardProps {
  campaigns: CustomerCampaignSummary[];
  lang: string;
}
function RecentCampaignsCard({ campaigns, lang }: RecentCampaignsCardProps): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="none">
      <div className="px-4.5 py-3.5 border-b border-line-soft flex justify-between items-center">
        <div className="text-[13px] font-semibold text-text">
          {t('acd_recent_campaigns_title')}
        </div>
        <span className="text-[12px] text-text-3">
          {t('acd_recent_campaigns_sub', { count: campaigns.length })}
        </span>
      </div>
      {campaigns.length === 0 ? (
        <div className="p-10 text-center text-[13px] text-text-3">
          {t('acd_recent_campaigns_empty')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                <th className="px-4.5 py-2 font-medium">{t('acd_camp_col_name')}</th>
                <th className="px-4.5 py-2 font-medium">{t('acd_camp_col_status')}</th>
                <th className="px-4.5 py-2 font-medium text-right">{t('acd_camp_col_recipients')}</th>
                <th className="px-4.5 py-2 font-medium text-right">{t('acd_camp_col_delivery_rate')}</th>
                <th className="px-4.5 py-2 font-medium text-right">{t('acd_camp_col_cost')}</th>
                <th className="px-4.5 py-2 font-medium">{t('acd_camp_col_when')}</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const sent = c.delivered_count + c.failed_count;
                const deliveryRate = sent > 0 ? (c.delivered_count / sent) * 100 : null;
                return (
                  <tr key={c.id} className="border-b border-line-soft last:border-0">
                    <td className="px-4.5 py-3 text-text font-medium truncate max-w-[260px]">
                      {c.name}
                    </td>
                    <td className="px-4.5 py-3">
                      <StatusPill status={c.status as CampaignStatus} />
                    </td>
                    <td className="px-4.5 py-3 num text-right text-text">
                      {c.total_recipients.toLocaleString(lang)}
                    </td>
                    <td className="px-4.5 py-3 num text-right text-text-2 text-[12px]">
                      {deliveryRate !== null ? `${deliveryRate.toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-4.5 py-3 num text-right text-text">
                      {formatCurrency(c.total_cost, lang)}
                    </td>
                    <td className="px-4.5 py-3 text-text-3 text-[12px] whitespace-nowrap">
                      {formatRelativeTime(c.created_at, lang)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-users card
// ---------------------------------------------------------------------------
function SubUsersCard({ users }: { users: CustomerSubUser[] }): JSX.Element {
  const { t } = useTranslation('default');
  // Click-to-expand state: which user's memberships panel is open. Only
  // one open at a time keeps the card compact.
  const [openUserId, setOpenUserId] = useState<number | null>(null);

  return (
    <Card padding="md">
      <div className="flex justify-between items-center mb-3.5">
        <div className="text-[13px] font-semibold text-text">
          {t('acd_users_title')}
          <span className="num text-text-3 font-normal ml-1.5">{users.length}</span>
        </div>
      </div>
      {users.length === 0 ? (
        <p className="text-[13px] text-text-3 py-2">{t('acd_users_empty')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {users.map((u) => (
            <div key={u.id} className="rounded-md border border-line-soft bg-surface-2 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenUserId((cur) => (cur === u.id ? null : u.id))}
                className="w-full flex items-center gap-3 px-2.5 py-2 hover:bg-surface-3 text-left"
                aria-expanded={openUserId === u.id}
              >
                <UserAvatar name={u.full_name || u.email} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-text font-medium truncate">
                    {u.full_name || u.email}
                  </div>
                  {u.full_name && (
                    <div className="mono text-[11.5px] text-text-3 truncate">{u.email}</div>
                  )}
                </div>
                <Badge variant="neutral">{t(`acd_role_${u.role}`)}</Badge>
                <span className="text-[11px] text-text-4">
                  {openUserId === u.id ? '▾' : '▸'}
                </span>
              </button>
              {openUserId === u.id && (
                <div className="border-t border-line-soft px-3 py-3 bg-surface">
                  <UserMembershipsPanel userId={u.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Memberships panel for one user. Shown inline when the operator
// expands a user row in SubUsersCard.
// ---------------------------------------------------------------------------
function UserMembershipsPanel({ userId }: { userId: number }): JSX.Element {
  const { t } = useTranslation('default');
  const list = useUserMemberships(userId);
  const assignable = useAssignableTenants(userId);
  const add = useAddMembership(userId);
  const remove = useRemoveMembership(userId);

  const [showAdd, setShowAdd] = useState(false);
  const [pickTenant, setPickTenant] = useState(0);
  const [pickRole, setPickRole] = useState<MembershipRole>('operator');
  const [error, setError] = useState<string | null>(null);

  const items = list.data?.items ?? [];
  const tenants = assignable.data?.items ?? [];

  const reset = (): void => {
    setShowAdd(false);
    setPickTenant(0);
    setPickRole('operator');
    setError(null);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    if (pickTenant === 0) {
      setError(t('amb_err_pick_tenant'));
      return;
    }
    try {
      await add.mutateAsync({ tenantId: pickTenant, role: pickRole });
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('amb_err_generic'));
    }
  };

  const onRemove = async (tenantId: number): Promise<void> => {
    setError(null);
    try {
      await remove.mutateAsync(tenantId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('amb_err_generic'));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium">
          {t('amb_title')}
        </div>
        {!showAdd && tenants.length > 0 && (
          <Button size="sm" variant="secondary" onClick={() => setShowAdd(true)}>
            <Icon.plus width="12" height="12" />
            {t('amb_add')}
          </Button>
        )}
      </div>

      {list.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : items.length === 0 ? (
        <p className="text-[12px] text-text-4 italic">{t('amb_empty')}</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between gap-2 px-2 py-1.5 bg-surface-2 rounded text-[12px]"
            >
              <div className="flex-1 flex items-center gap-2 min-w-0">
                <span className="text-text font-medium truncate">{m.tenant_name}</span>
                <Badge variant="neutral">{m.tenant_type}</Badge>
                <Badge variant="neutral">{m.role}</Badge>
              </div>
              <button
                type="button"
                onClick={() => void onRemove(m.tenant_id)}
                disabled={remove.isPending}
                className="text-[11px] text-danger hover:underline disabled:opacity-50"
                title={t('amb_remove')}
              >
                {t('amb_remove')}
              </button>
            </div>
          ))}
        </div>
      )}

      {!showAdd && tenants.length === 0 && items.length > 0 && (
        <p className="text-[11px] text-text-4 italic mt-2">{t('amb_all_assigned')}</p>
      )}

      {showAdd && (
        <div className="mt-3 pt-3 border-t border-line-soft space-y-2">
          {error && (
            <div className="px-2 py-1.5 bg-danger-bg border border-danger-bd rounded text-[12px] text-danger">
              {error}
            </div>
          )}
          <div>
            <label className="block text-[11px] text-text-4 mb-1">{t('amb_field_tenant')}</label>
            <select
              value={pickTenant || ''}
              onChange={(e) => setPickTenant(Number(e.target.value) || 0)}
              className="w-full h-8 px-2 text-[12px] rounded bg-surface border border-line text-text outline-none focus:border-volt-500"
            >
              <option value="">{t('amb_field_tenant_choose')}</option>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>
                  {tn.name} ({tn.type})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-text-4 mb-1">{t('amb_field_role')}</label>
            <select
              value={pickRole}
              onChange={(e) => setPickRole(e.target.value as MembershipRole)}
              className="w-full h-8 px-2 text-[12px] rounded bg-surface border border-line text-text outline-none focus:border-volt-500"
            >
              <option value="operator">operator</option>
              <option value="admin">admin</option>
              <option value="viewer">viewer</option>
              <option value="owner">owner</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="secondary" onClick={reset} disabled={add.isPending}>
              {t('amb_cancel')}
            </Button>
            <Button size="sm" onClick={() => void submit()} disabled={add.isPending}>
              {add.isPending ? t('amb_adding') : t('amb_save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function UserAvatar({ name }: { name: string }): JSX.Element {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      aria-hidden="true"
      className="w-7 h-7 rounded-full grid place-items-center text-white text-[11px] font-bold flex-shrink-0"
      style={{ background: 'linear-gradient(135deg, var(--volt-500), var(--volt-700))' }}
    >
      {initial}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Plan card — current subscription + dropdown to change. Auto-saves on
// select; the parent query invalidation refreshes the badge inline.
// Lazy-loads the plans catalog only when this card mounts (the customer
// detail page is the only caller right now).
// ---------------------------------------------------------------------------
interface PlanCardProps {
  customerId: number;
  currentPlan?: CustomerPlanRef;
  lang: string;
}
function PlanCard({ customerId, currentPlan, lang }: PlanCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const plans = usePlans();
  const assign = useAssignCustomerPlan();
  const [error, setError] = useState<string | null>(null);

  const onChange = async (next: string): Promise<void> => {
    setError(null);
    const planId = next === '' ? null : Number(next);
    try {
      await assign.mutateAsync({ customerId, planId });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('acd_plan_err_generic'));
    }
  };

  const activePlans = (plans.data?.items ?? []).filter((p) => p.is_active);

  return (
    <Card padding="md">
      <div className="flex justify-between items-center mb-3.5">
        <div className="text-[13px] font-semibold text-text">
          {t('acd_plan_title')}
        </div>
        {currentPlan ? (
          <Badge variant={currentPlan.color === 'volt' ? 'success' : 'neutral'}>
            {currentPlan.name}
          </Badge>
        ) : (
          <Badge variant="neutral">{t('acd_plan_none_badge')}</Badge>
        )}
      </div>

      {plans.isLoading ? (
        <Skeleton className="h-9 w-full" />
      ) : (
        <select
          value={currentPlan?.id ?? ''}
          onChange={(e) => void onChange(e.target.value)}
          disabled={assign.isPending}
          className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-60"
        >
          <option value="">{t('acd_plan_none_option')}</option>
          {activePlans.map((p) => (
            <option key={p.id} value={p.id}>
              {`${p.name} · ${formatCurrency(p.monthly_fee, lang)} ${t('acd_plan_per_month')}`}
            </option>
          ))}
        </select>
      )}

      {error && (
        <p className="mt-2 text-[12px] text-danger">{error}</p>
      )}

      {!plans.isLoading && activePlans.length === 0 && (
        <p className="mt-2 text-[12px] text-text-3">
          {t('acd_plan_no_catalog')}
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Rate cards — multi-card cascade assignment per ADR-008. Cards are
// tried in card_priority ASC at dispatch time; the first card with a
// matching rate wins. The "Test prefix" widget runs the same resolution
// the dispatcher uses so the operator can verify their setup before
// sending real traffic.
// ---------------------------------------------------------------------------
function RateCardsCard({ tenantId }: { tenantId: number }): JSX.Element {
  const { t } = useTranslation('default');
  const list = useTenantRateCards(tenantId);
  const allCards = useRateCards();
  const assign = useAssignTenantRateCard(tenantId);
  const unassign = useUnassignTenantRateCard(tenantId);
  const reorder = useReorderTenantRateCard(tenantId);
  const test = useTestPrefix(tenantId);

  const [adding, setAdding] = useState(false);
  const [pickCardId, setPickCardId] = useState(0);
  const [pickPriority, setPickPriority] = useState(100);
  const [error, setError] = useState<string | null>(null);

  const [testDest, setTestDest] = useState('');
  const [testResult, setTestResult] = useState<{
    found: boolean;
    text: string;
  } | null>(null);

  const items = list.data?.items ?? [];
  const assignableCards = (allCards.data?.items ?? []).filter(
    (c) => !items.some((a) => a.rate_card_id === c.id),
  );

  const reset = (): void => {
    setAdding(false);
    setPickCardId(0);
    setPickPriority(100);
    setError(null);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    if (pickCardId === 0) {
      setError(t('artc_err_pick_card'));
      return;
    }
    try {
      await assign.mutateAsync({
        rate_card_id: pickCardId,
        card_priority: pickPriority,
      });
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('artc_err_generic'));
    }
  };

  const runTest = async (): Promise<void> => {
    const dest = testDest.trim();
    if (!dest) return;
    setTestResult(null);
    try {
      const r = await test.mutateAsync({ destination: dest });
      if (!r.found) {
        setTestResult({ found: false, text: t('artc_test_no_match') });
      } else {
        setTestResult({
          found: true,
          text: t('artc_test_match', {
            carrier: r.provider_name,
            card: r.rate_card_name,
            prefix: r.prefix,
            sell: (r.sell_price ?? 0).toFixed(6),
            buy: (r.buy_cost ?? 0).toFixed(6),
          }),
        });
      }
    } catch (err) {
      setTestResult({
        found: false,
        text: err instanceof ApiError ? err.message : t('artc_err_generic'),
      });
    }
  };

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-3.5">
        <div className="text-[13px] font-semibold text-text">{t('artc_title')}</div>
        {!adding && assignableCards.length > 0 && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Icon.plus width="12" height="12" />
            {t('artc_add')}
          </Button>
        )}
      </div>
      <p className="text-[12px] text-text-3 mb-3 leading-snug">{t('artc_help')}</p>

      {list.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : items.length === 0 && !adding ? (
        <p className="text-[12px] text-text-4 italic">{t('artc_empty')}</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((a) => (
            <AssignmentRow
              key={a.rate_card_id}
              assignment={a}
              busy={unassign.isPending || reorder.isPending}
              onPriorityChange={async (newP) => {
                try {
                  await reorder.mutateAsync({ cardId: a.rate_card_id, cardPriority: newP });
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : t('artc_err_generic'));
                }
              }}
              onUnassign={async () => {
                try {
                  await unassign.mutateAsync(a.rate_card_id);
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : t('artc_err_generic'));
                }
              }}
            />
          ))}
        </div>
      )}

      {adding && (
        <div className="mt-3 pt-3 border-t border-line-soft space-y-2">
          {error && (
            <div className="px-2 py-1.5 bg-danger-bg border border-danger-bd rounded text-[12px] text-danger">
              {error}
            </div>
          )}
          <div>
            <label className="block text-[11px] text-text-4 mb-1">{t('artc_field_card')}</label>
            <select
              value={pickCardId || ''}
              onChange={(e) => setPickCardId(Number(e.target.value) || 0)}
              className="w-full h-8 px-2 text-[12px] rounded bg-surface border border-line text-text outline-none focus:border-volt-500"
            >
              <option value="">{t('artc_field_card_choose')}</option>
              {assignableCards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.strategy.toUpperCase()})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-text-4 mb-1">{t('artc_field_priority')}</label>
            <input
              type="number"
              min={1}
              value={pickPriority}
              onChange={(e) => setPickPriority(Number(e.target.value) || 100)}
              className="w-full h-8 px-2 text-[12px] num rounded bg-surface border border-line text-text outline-none focus:border-volt-500"
            />
            <p className="text-[10px] text-text-4 mt-1">{t('artc_field_priority_hint')}</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="secondary" onClick={reset} disabled={assign.isPending}>
              {t('artc_cancel')}
            </Button>
            <Button size="sm" onClick={() => void submit()} disabled={assign.isPending}>
              {assign.isPending ? t('artc_saving') : t('artc_save')}
            </Button>
          </div>
        </div>
      )}

      {!adding && assignableCards.length === 0 && items.length > 0 && (
        <p className="text-[11px] text-text-4 italic mt-3">{t('artc_all_assigned')}</p>
      )}

      {/* Test prefix simulator */}
      {items.length > 0 && (
        <div className="mt-3 pt-3 border-t border-line-soft">
          <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium mb-2">
            {t('artc_test_title')}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={testDest}
              onChange={(e) => setTestDest(e.target.value)}
              placeholder="+13055551234"
              className="flex-1 h-8 px-2 mono text-[12px] rounded bg-surface border border-line text-text outline-none focus:border-volt-500"
            />
            <Button size="sm" variant="secondary" onClick={() => void runTest()} disabled={test.isPending}>
              {test.isPending ? t('artc_test_running') : t('artc_test_run')}
            </Button>
          </div>
          {testResult && (
            <div
              className={cn(
                'mt-2 px-2 py-1.5 rounded text-[11px] leading-snug',
                testResult.found ? 'bg-success-bg text-success border border-success-bd' : 'bg-warn-bg text-warn border border-warn-bd',
              )}
            >
              {testResult.text}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

interface AssignmentRowProps {
  assignment: TenantRateCardAssignment;
  busy: boolean;
  onPriorityChange: (newP: number) => Promise<void>;
  onUnassign: () => Promise<void>;
}
function AssignmentRow({ assignment, busy, onPriorityChange, onUnassign }: AssignmentRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const [prio, setPrio] = useState(assignment.card_priority);
  const [editingPrio, setEditingPrio] = useState(false);

  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-surface-2 rounded text-[12px]">
      <div className="flex-1 flex items-center gap-2 min-w-0">
        {editingPrio ? (
          <input
            type="number"
            min={1}
            value={prio}
            onChange={(e) => setPrio(Number(e.target.value) || 100)}
            onBlur={async () => {
              setEditingPrio(false);
              if (prio !== assignment.card_priority) {
                await onPriorityChange(prio);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            disabled={busy}
            className="w-12 h-6 px-1 num text-[11px] rounded bg-surface border border-line text-text outline-none focus:border-volt-500"
            autoFocus
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingPrio(true)}
            className="mono text-[11px] font-semibold text-text-2 bg-surface px-1.5 py-0.5 rounded border border-line hover:border-text-4"
            title={t('artc_priority_click_edit')}
          >
            #{assignment.card_priority}
          </button>
        )}
        <span className="text-text font-medium truncate">{assignment.rate_card_name}</span>
        <StrategyBadge strategy={assignment.strategy} />
      </div>
      <button
        type="button"
        onClick={() => void onUnassign()}
        disabled={busy}
        className="text-[11px] text-danger hover:underline disabled:opacity-50"
      >
        {t('artc_remove')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TTS budget card — per-tenant monthly character cap for voice TTS renders
// (migration 000058). Shows the current cap, usage this period, the period
// reset date, and an "Edit cap" button that opens an inline modal mirroring
// the credit-limit / low-balance-threshold editors above.
//
// 0 is a legal cap value — the renderer treats `chars_used >= cap` as
// over-budget, so 0 effectively disables TTS for the tenant. We don't
// special-case that in the UI beyond letting the operator type it.
// ---------------------------------------------------------------------------
interface TTSBudgetCardProps {
  customerId: number;
  budget: CustomerTTSBudget;
  lang: string;
}

function TTSBudgetCard({ customerId, budget, lang }: TTSBudgetCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const setCap = useSetTTSCap();
  const [edit, setEdit] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Defensive clamp: if a stale row somehow reports more chars_used than
  // cap (clock skew at reset, manual SQL edit), don't render a >100%
  // progress bar — Progress clamps internally but the percent text we
  // show next to it should match what the user sees in the bar.
  const cap = Math.max(0, budget.cap);
  const used = Math.max(0, budget.chars_used);
  const ratio = cap > 0 ? Math.min(1, used / cap) : 0;
  const percentText = cap > 0 ? `${(ratio * 100).toFixed(1)}%` : '—';
  const resetAtText = new Date(budget.reset_at).toLocaleDateString(lang, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const onSave = async (): Promise<void> => {
    if (edit === null) return;
    setError(null);
    if (!Number.isFinite(edit) || edit < 0) {
      setError(t('acd_tts_err_negative'));
      return;
    }
    if (edit > 10_000_000) {
      setError(t('acd_tts_err_max'));
      return;
    }
    try {
      await setCap.mutateAsync({ customerId, cap: Math.floor(edit) });
      setEdit(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('acd_tts_err_generic'));
    }
  };

  return (
    <Card padding="md">
      <div className="flex justify-between items-center mb-3.5">
        <div className="text-[13px] font-semibold text-text">
          {t('acd_tts_title')}
        </div>
        <button
          type="button"
          onClick={() => {
            setEdit(cap);
            setError(null);
          }}
          className="text-[12px] text-volt-500 font-medium hover:underline"
        >
          {t('acd_tts_edit')}
        </button>
      </div>

      <div className="text-[13px] text-text-2 mb-1">
        <span className="num font-medium text-text">{cap.toLocaleString(lang)}</span>{' '}
        <span className="text-text-3">{t('acd_tts_cap_suffix')}</span>
      </div>
      <div className="text-[12px] text-text-3 mb-2">
        {t('acd_tts_usage', {
          used: used.toLocaleString(lang),
          cap: cap.toLocaleString(lang),
          pct: percentText,
        })}
      </div>
      <Progress value={used} total={cap > 0 ? cap : 1} height={6} />
      <div className="text-[11.5px] text-text-3 mt-2">
        {t('acd_tts_resets', { when: resetAtText })}
      </div>

      {edit !== null && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tts-modal-title"
          onClick={() => {
            if (!setCap.isPending) {
              setEdit(null);
              setError(null);
            }
          }}
        >
          <div
            className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[440px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-line">
              <h2 id="tts-modal-title" className="display text-[16px] font-semibold text-text">
                {t('acd_tts_modal_title')}
              </h2>
            </div>
            <div className="px-5 py-4 text-[13px] text-text-2 leading-relaxed">
              <p className="mb-3">{t('acd_tts_modal_desc')}</p>
              <label className="block">
                <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
                  {t('acd_tts_modal_label')}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="0"
                  max="10000000"
                  autoFocus
                  value={edit}
                  onChange={(e) => setEdit(Number(e.target.value))}
                  disabled={setCap.isPending}
                  className={cn(
                    'h-10 w-full px-3 text-sm bg-surface text-text border rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50',
                    error ? 'border-danger' : 'border-line-strong',
                  )}
                />
                <p className="text-[11.5px] text-text-3 mt-1.5">{t('acd_tts_modal_hint')}</p>
              </label>
              {error && <p className="text-[12px] text-danger mt-2">{error}</p>}
            </div>
            <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setEdit(null);
                  setError(null);
                }}
                disabled={setCap.isPending}
              >
                {t('ac_cancel')}
              </Button>
              <Button onClick={() => void onSave()} disabled={setCap.isPending}>
                {setCap.isPending ? t('acd_tts_modal_saving') : t('acd_tts_modal_save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Kai follow-up SMS toggle — lets the platform admin opt a tenant out of
// the post-call follow-up SMS. The ai_followup_sms worker checks this flag
// on every scan; setting it false stops follow-up SMSes from the next tick
// without touching any voice configuration or existing enriched insights.
//
// The toggle is always visible regardless of feature_voice so the operator
// can pre-configure it before enrolling the tenant in voice, and because
// a UI that conditionally shows/hides a toggle based on another flag is
// harder to reason about than one that's always present. A toggle with no
// effect (SMS-only tenant) is harmless.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Per-module toggle cards — Voice + SMS. Same pattern as the Follow-up SMS
// toggle below: small card, single checkbox, optimistic flip, inline error
// on failure. Replaces the manual SQL UPDATE we ran per-customer until now.
// The SMS toggle's runtime side-effect today is portal hygiene only (it
// hides the sidebar items via /v1/features); a future PR will gate the
// dispatcher path too.
// ---------------------------------------------------------------------------
interface FeatureToggleCardProps {
  customerId: number;
  enabled: boolean;
}

// SpamGuard screening posture — platform-only control over how hard
// SpamGuard screens this tenant's campaigns at launch. 'trusted' skips the
// screen entirely (vetted / first-party senders), 'normal' is the default,
// 'strict' sends even a clean score to the review queue. The numeric spam
// score is never surfaced — only the posture — so a tenant can't tune
// messaging to slip under the threshold. Saved via the platform-only
// PATCH /admin/tenants/{id}/spamguard-level.
const SPAMGUARD_LEVELS: SpamguardLevel[] = ['trusted', 'normal', 'strict'];

interface SpamguardLevelCardProps {
  customerId: number;
  level: SpamguardLevel;
}

function SpamguardLevelCard({ customerId, level }: SpamguardLevelCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const setLevel = useSetSpamguardLevel();
  const [error, setError] = useState<string | null>(null);

  const onChange = async (next: SpamguardLevel): Promise<void> => {
    if (next === level) return;
    setError(null);
    try {
      await setLevel.mutateAsync({ tenantId: customerId, level: next });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('acd_spamguard_err_generic'));
    }
  };

  return (
    <Card padding="md">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-text">
            {t('acd_spamguard_title')}
          </div>
          <p className="text-[12px] text-text-3 mt-1 leading-snug">
            {t(`acd_spamguard_help_${level}`)}
          </p>
          {error && <p className="text-[12px] text-danger mt-1.5">{error}</p>}
        </div>
        <select
          value={level}
          disabled={setLevel.isPending}
          onChange={(e) => void onChange(e.target.value as SpamguardLevel)}
          className="h-8 px-2 text-xs bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 disabled:opacity-50 flex-shrink-0"
          aria-label={t('acd_spamguard_title')}
        >
          {SPAMGUARD_LEVELS.map((lv) => (
            <option key={lv} value={lv}>
              {t(`acd_spamguard_level_${lv}`)}
            </option>
          ))}
        </select>
      </div>
    </Card>
  );
}

function FeatureVoiceToggleCard({ customerId, enabled }: FeatureToggleCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const setFlag = useSetFeatureVoice();
  const [error, setError] = useState<string | null>(null);

  const onChange = async (next: boolean): Promise<void> => {
    setError(null);
    try {
      await setFlag.mutateAsync({ customerId, enabled: next });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('acd_feature_voice_err_generic'));
    }
  };

  return (
    <Card padding="md">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-text">
            {t('acd_feature_voice_title')}
          </div>
          <p className="text-[12px] text-text-3 mt-1 leading-snug">
            {t('acd_feature_voice_help')}
          </p>
          {error && (
            <p className="text-[12px] text-danger mt-1.5">{error}</p>
          )}
        </div>
        <label className="flex items-center gap-2 flex-shrink-0 cursor-pointer select-none">
          <span className="text-[12px] text-text-3">
            {t('acd_feature_voice_toggle_label')}
          </span>
          <input
            type="checkbox"
            checked={enabled}
            disabled={setFlag.isPending}
            onChange={(e) => void onChange(e.target.checked)}
            className="w-4 h-4 rounded border border-line-strong accent-volt-500 disabled:opacity-50 cursor-pointer"
            aria-label={t('acd_feature_voice_toggle_label')}
          />
        </label>
      </div>
    </Card>
  );
}

function FeatureSMSToggleCard({ customerId, enabled }: FeatureToggleCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const setFlag = useSetFeatureSMS();
  const [error, setError] = useState<string | null>(null);

  const onChange = async (next: boolean): Promise<void> => {
    setError(null);
    try {
      await setFlag.mutateAsync({ customerId, enabled: next });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('acd_feature_sms_err_generic'));
    }
  };

  return (
    <Card padding="md">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-text">
            {t('acd_feature_sms_title')}
          </div>
          <p className="text-[12px] text-text-3 mt-1 leading-snug">
            {t('acd_feature_sms_help')}
          </p>
          {error && (
            <p className="text-[12px] text-danger mt-1.5">{error}</p>
          )}
        </div>
        <label className="flex items-center gap-2 flex-shrink-0 cursor-pointer select-none">
          <span className="text-[12px] text-text-3">
            {t('acd_feature_sms_toggle_label')}
          </span>
          <input
            type="checkbox"
            checked={enabled}
            disabled={setFlag.isPending}
            onChange={(e) => void onChange(e.target.checked)}
            className="w-4 h-4 rounded border border-line-strong accent-volt-500 disabled:opacity-50 cursor-pointer"
            aria-label={t('acd_feature_sms_toggle_label')}
          />
        </label>
      </div>
    </Card>
  );
}

interface FollowupSMSToggleCardProps {
  customerId: number;
  enabled: boolean;
}

function FollowupSMSToggleCard({ customerId, enabled }: FollowupSMSToggleCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const setFlag = useSetFeatureFollowupSMS();
  const [error, setError] = useState<string | null>(null);

  const onChange = async (next: boolean): Promise<void> => {
    setError(null);
    try {
      await setFlag.mutateAsync({ customerId, enabled: next });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('acd_followup_sms_err_generic'));
    }
  };

  return (
    <Card padding="md">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-text">
            {t('acd_followup_sms_title')}
          </div>
          <p className="text-[12px] text-text-3 mt-1 leading-snug">
            {t('acd_followup_sms_help')}
          </p>
          {error && (
            <p className="text-[12px] text-danger mt-1.5">{error}</p>
          )}
        </div>
        <label className="flex items-center gap-2 flex-shrink-0 cursor-pointer select-none">
          <span className="text-[12px] text-text-3">
            {t('acd_followup_sms_toggle_label')}
          </span>
          <input
            type="checkbox"
            checked={enabled}
            disabled={setFlag.isPending}
            onChange={(e) => void onChange(e.target.checked)}
            className="w-4 h-4 rounded border border-line-strong accent-volt-500 disabled:opacity-50 cursor-pointer"
            aria-label={t('acd_followup_sms_toggle_label')}
          />
        </label>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// CTIA compliance templates card — lets the reseller override the
// hardcoded HELP/STOP/START SMS auto-reply bodies the dlr-receiver fires
// when a destinatario texts the corresponding keyword. The CTIA Short
// Code Monitoring Handbook (and 10DLC operator guidelines) require the
// HELP reply to identify the program brand. Shipping a white-label
// customer under the EzSender brand violates the spec.
//
// Empty textarea = "use platform default" (the value is sent as empty
// string and the backend stores NULL; the dlr-receiver treats both
// identically). The placeholder shows the actual default copy so the
// operator sees exactly what their destinatarios receive today.
//
// We surface a small inline help tooltip per field with the CTIA
// rationale + the verbatim default. No live preview vs. saved state —
// the textareas show the current saved override, and the Save button
// is disabled until the values diverge from the loaded data.
// ---------------------------------------------------------------------------
interface ComplianceTemplatesCardProps {
  customerId: number;
  help: string;
  stop: string;
  start: string;
}

function ComplianceTemplatesCard({
  customerId,
  help,
  stop,
  start,
}: ComplianceTemplatesCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const setTemplates = useSetComplianceTemplates();
  const [helpVal, setHelpVal] = useState(help);
  const [stopVal, setStopVal] = useState(stop);
  const [startVal, setStartVal] = useState(start);
  const [error, setError] = useState<string | null>(null);
  const [savedDefaults, setSavedDefaults] = useState<{ help: string; stop: string; start: string } | null>(null);

  // Re-sync local input state when the saved values change (e.g. after a
  // successful PATCH, the parent query refetches and re-passes the
  // server-trimmed body as a fresh prop). Without this the dirty flag
  // would stay sticky-true if the server trimmed whitespace from what
  // the operator typed, and a subsequent edit on another tab wouldn't
  // surface here either.
  useEffect(() => {
    setHelpVal(help);
    setStopVal(stop);
    setStartVal(start);
  }, [help, stop, start]);

  // Defaults from the server are only known after the first successful
  // PATCH; until then we hardcode the same copy the dlr-receiver uses so
  // the placeholder is meaningful on the initial paint. Kept verbatim
  // in sync with workers/cmd/dlr-receiver/main.go::{helpReply, stopReply,
  // startReply} and api/internal/services/admin_customers.go::DefaultComplianceTemplates.
  const defaults = savedDefaults ?? {
    help: t('acd_compliance_help_default_fallback'),
    stop: t('acd_compliance_stop_default_fallback'),
    start: t('acd_compliance_start_default_fallback'),
  };

  const dirty = helpVal !== help || stopVal !== stop || startVal !== start;

  const onSave = async (): Promise<void> => {
    setError(null);
    try {
      const resp = await setTemplates.mutateAsync({
        customerId,
        helpTemplate: helpVal,
        stopTemplate: stopVal,
        startTemplate: startVal,
      });
      setSavedDefaults(resp.defaults);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('acd_compliance_err_generic'));
    }
  };

  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-[13px] font-semibold text-text">
            {t('acd_compliance_title')}
          </div>
          <p className="text-[12px] text-text-3 mt-1 leading-snug">
            {t('acd_compliance_help')}
          </p>
        </div>
      </div>

      <ComplianceTemplateField
        label={t('acd_compliance_help_label')}
        helpText={t('acd_compliance_help_field_help', { defaultBody: defaults.help })}
        value={helpVal}
        placeholder={defaults.help}
        onChange={setHelpVal}
        disabled={setTemplates.isPending}
      />
      <ComplianceTemplateField
        label={t('acd_compliance_stop_label')}
        helpText={t('acd_compliance_stop_field_help', { defaultBody: defaults.stop })}
        value={stopVal}
        placeholder={defaults.stop}
        onChange={setStopVal}
        disabled={setTemplates.isPending}
      />
      <ComplianceTemplateField
        label={t('acd_compliance_start_label')}
        helpText={t('acd_compliance_start_field_help', { defaultBody: defaults.start })}
        value={startVal}
        placeholder={defaults.start}
        onChange={setStartVal}
        disabled={setTemplates.isPending}
      />

      {error && (
        <p className="text-[12px] text-danger mt-2">{error}</p>
      )}

      <div className="flex items-center justify-end gap-2 mt-3">
        <Button
          variant="ghost"
          size="sm"
          disabled={setTemplates.isPending || !dirty}
          onClick={() => {
            setHelpVal(help);
            setStopVal(stop);
            setStartVal(start);
            setError(null);
          }}
        >
          {t('acd_compliance_cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={setTemplates.isPending || !dirty}
          onClick={() => void onSave()}
        >
          {setTemplates.isPending
            ? t('acd_compliance_saving')
            : t('acd_compliance_save')}
        </Button>
      </div>
    </Card>
  );
}

interface ComplianceTemplateFieldProps {
  label: string;
  helpText: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  disabled: boolean;
}

function ComplianceTemplateField({
  label,
  helpText,
  value,
  placeholder,
  onChange,
  disabled,
}: ComplianceTemplateFieldProps): JSX.Element {
  // maxLength on <textarea> is in UTF-16 code units, which matches the
  // server-side rune count for the BMP characters real CTIA copy uses
  // (Latin letters + Spanish accents). It hard-caps the input so the
  // operator can't paste an essay; the counter below makes the limit
  // visible while they type.
  return (
    <div className="mb-3 last:mb-0">
      <label className="flex items-center gap-1.5 text-[12px] font-medium text-text mb-1">
        <span>{label}</span>
        <span
          className="text-text-3 cursor-help"
          title={helpText}
          aria-label={helpText}
        >
          <Icon.help width="13" height="13" />
        </span>
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={2}
        maxLength={160}
        className="w-full px-2.5 py-1.5 text-[12px] rounded border border-line-strong bg-surface text-text placeholder:text-text-4 focus:outline-none focus:ring-1 focus:ring-volt-500 disabled:opacity-50 resize-y mono"
      />
      <div className="flex justify-end mt-0.5">
        <span className="text-[11px] text-text-3 num">{value.length}/160</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account info card — KV pairs the operator scans without scrolling.
// ---------------------------------------------------------------------------
function AccountCard({
  customer,
  lang,
}: {
  customer: { uuid: string; country?: string; timezone?: string; created_at: string; owner_email?: string };
  lang: string;
}): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="md">
      <div className="text-[13px] font-semibold text-text mb-3.5">
        {t('acd_account_title')}
      </div>
      <KV label={t('acd_account_workspace_id')} value={<span className="mono">{customer.uuid}</span>} />
      <KV label={t('acd_account_country')} value={customer.country || '—'} />
      <KV label={t('acd_account_timezone')} value={customer.timezone || '—'} />
      <KV
        label={t('acd_account_created')}
        value={new Date(customer.created_at).toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric' })}
      />
      <KV
        label={t('acd_account_owner')}
        value={
          customer.owner_email ? (
            <span className="mono text-volt-700">{customer.owner_email}</span>
          ) : (
            '—'
          )
        }
        last
      />
    </Card>
  );
}

interface KVProps {
  label: string;
  value: React.ReactNode;
  last?: boolean;
}
function KV({ label, value, last = false }: KVProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex justify-between items-center py-2 text-[13px]',
        !last && 'border-b border-line-soft',
      )}
    >
      <span className="text-text-3">{label}</span>
      <span className="text-text font-medium text-right">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity placeholder — keeps the right column balanced. The real timeline
// lands when audit_log gets a service + handler (separate workstream).
// ---------------------------------------------------------------------------
function ActivityPlaceholderCard(): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="md">
      <div className="text-[13px] font-semibold text-text mb-1">
        {t('acd_activity_title')}
      </div>
      <p className="text-[12px] text-text-3">{t('acd_activity_placeholder')}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Banner — used for the suspended state at the top of the page. The body
// keeps the same shape as the impersonation banner so the operator's eye
// learns one pattern across admin pages.
// ---------------------------------------------------------------------------
interface BannerProps {
  tone: 'warn';
  title: string;
  body: string;
  action?: React.ReactNode;
}
function Banner({ title, body, action }: BannerProps): JSX.Element {
  return (
    <div className="flex items-center gap-3 px-6 py-2.5 bg-warn-bg border-b border-warn-bd text-[13px]">
      <Icon.warn width="18" height="18" style={{ color: 'var(--warn)', flexShrink: 0 }} />
      <div className="flex-1">
        <strong className="text-warn font-semibold">{title}</strong>
        <span className="text-text-2 ml-2">{body}</span>
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton — keeps the page width stable so the header doesn't
// jump when data lands.
// ---------------------------------------------------------------------------
function DetailSkeleton(): JSX.Element {
  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto w-full">
      <Skeleton className="h-4 w-32 mb-6" />
      <div className="flex items-start gap-4 mb-8">
        <Skeleton className="w-14 h-14 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <div className="grid grid-cols-[2fr_1fr] gap-4">
        <Skeleton className="h-[320px]" />
        <Skeleton className="h-[320px]" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice rate card. Mirrors the SMS RateCardsCard visual pattern (title +
// "Assign card" button → inline picker → row per assigned card) so both
// channels share the same admin mental model. Voice only supports ONE
// primary card per tenant today (no cascade like SMS), but the layout
// scales if we add secondaries later.
// ---------------------------------------------------------------------------
function VoiceRateCardCard({ tenantId }: { tenantId: number }): JSX.Element {
  const { t } = useTranslation('default');
  const list = useTenantVoiceRateCards(tenantId);
  const allCards = useVoiceRateCards();
  const assign = useAssignVoiceRateCard();
  const unassign = useUnassignVoiceRateCard();

  const [adding, setAdding] = useState(false);
  const [pickCardId, setPickCardId] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const assignments = list.data ?? [];
  // Hide already-assigned cards from the picker (matches SMS pattern).
  const assignableCards = (allCards.data ?? []).filter(
    (c) => !assignments.some((a) => a.rate_card_id === c.id),
  );

  function reset() {
    setAdding(false);
    setPickCardId(0);
    setError(null);
  }

  async function submit() {
    if (pickCardId === 0) {
      setError(t('vrcc_err_pick_card'));
      return;
    }
    setError(null);
    try {
      await assign.mutateAsync({
        tenantID: tenantId,
        rateCardID: pickCardId,
        isPrimary: true,
      });
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vrcc_err_generic'));
    }
  }

  async function handleUnassign(cardID: number) {
    setError(null);
    try {
      await unassign.mutateAsync({ tenantID: tenantId, rateCardID: cardID });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vrcc_err_generic'));
    }
  }

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-3.5">
        <div className="text-[13px] font-semibold text-text">{t('vrcc_title')}</div>
        {!adding && assignableCards.length > 0 && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Icon.plus width="12" height="12" />
            {t('vrcc_add')}
          </Button>
        )}
      </div>
      <p className="text-[12px] text-text-3 mb-3 leading-snug">{t('vrcc_help')}</p>

      {list.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : assignments.length === 0 && !adding ? (
        <p className="text-[12px] text-text-4 italic">{t('vrcc_empty')}</p>
      ) : (
        <div className="space-y-1.5">
          {assignments.map((a) => (
            <div
              key={a.rate_card_id}
              className="flex items-center justify-between gap-2 px-2 py-1.5 bg-surface-2 rounded text-[12px]"
            >
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="text-text truncate">{a.card_name}</span>
                {a.is_primary && <Badge variant="success">{t('vrcc_primary_badge')}</Badge>}
                <span className="text-text-4">·</span>
                <span className="text-text-3 truncate">
                  {t('vrcc_default_trunk')}:{' '}
                  <strong className="text-text-2">{a.default_trunk_name}</strong>
                </span>
              </div>
              <button
                type="button"
                onClick={() => void handleUnassign(a.rate_card_id)}
                disabled={unassign.isPending}
                className="text-[11px] text-danger/80 hover:text-danger disabled:opacity-50 px-1.5"
              >
                {t('vrcc_unassign')}
              </button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="mt-3 pt-3 border-t border-line-soft space-y-2">
          {error && (
            <div className="px-2 py-1.5 bg-danger-bg border border-danger-bd rounded text-[12px] text-danger">
              {error}
            </div>
          )}
          <div>
            <label className="block text-[11px] text-text-4 mb-1">{t('vrcc_field_card')}</label>
            <select
              value={pickCardId || ''}
              onChange={(e) => setPickCardId(parseInt(e.target.value, 10) || 0)}
              className="w-full h-8 px-2 text-[12px] rounded bg-surface border border-line text-text outline-none focus:border-volt-500"
            >
              <option value="">{t('vrcc_field_card_choose')}</option>
              {assignableCards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="secondary" onClick={reset} disabled={assign.isPending}>
              {t('vrcc_cancel')}
            </Button>
            <Button size="sm" onClick={() => void submit()} disabled={assign.isPending}>
              {assign.isPending ? t('vrcc_saving') : t('vrcc_save')}
            </Button>
          </div>
        </div>
      )}

      {!adding && assignableCards.length === 0 && assignments.length === 0 && !list.isLoading && (
        <p className="mt-2 text-[12px] text-text-3">
          {t('vrcc_no_catalog')}{' '}
          <Link to="/admin/voice/rate-cards" className="text-volt-600 hover:underline">
            {t('vrcc_no_catalog_link')}
          </Link>
        </p>
      )}

      {error && !adding && <p className="mt-2 text-[12px] text-danger">{error}</p>}
    </Card>
  );
}

